import { useEffect, useMemo, useRef, useState } from 'react'
import { Download, FileUp, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { useImportQuickReplies, type QuickReply } from '@/hooks/use-quick-replies'
import {
  downloadCsv,
  parseQuickReplyCsv,
  serializeQuickRepliesCsv,
  type ParsedCsv,
  type PreviewRow,
} from './quick-reply-csv'

const MAX_ROWS = 1000
const MAX_BYTES = 2 * 1024 * 1024

const STATUS: Record<PreviewRow['status'], { variant: BadgeProps['variant'] }> = {
  ok: { variant: 'success' },
  exists: { variant: 'warning' },
  duplicate: { variant: 'secondary' },
  error: { variant: 'destructive' },
}

// Dữ liệu file mẫu (nội dung CSV) — giữ nguyên tiếng Việt
const SAMPLE = [
  { title: 'Chào khách mới', content: 'Dạ em chào anh/chị {{contact.firstName}}, em có thể hỗ trợ gì ạ?', category: 'Chào hỏi' }, // i18n-ignore
  { title: 'Xin số điện thoại', content: 'Anh/chị cho em xin số điện thoại để bên em tư vấn chi tiết nhé ạ.', category: 'Tư vấn' }, // i18n-ignore
]

/**
 * Nhập CSV: chọn file → xem trước từng dòng (lỗi / trùng / đã có) → nhập.
 * Server kiểm tra lại toàn bộ; phần xem trước chỉ để người dùng sửa file trước.
 */
export function QuickReplyImportDialog({
  open,
  onClose,
  library,
  canManageTeam,
}: {
  open: boolean
  onClose: () => void
  library: QuickReply[]
  canManageTeam: boolean
}) {
  const { t } = useTranslation('quickReplies')
  const importer = useImportQuickReplies()
  const fileRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState('')
  const [scope, setScope] = useState<'team' | 'personal'>(canManageTeam ? 'team' : 'personal')
  const [onDuplicate, setOnDuplicate] = useState<'skip' | 'overwrite'>('skip')

  // Quyền tải xong sau khi trang đã dựng dialog → chọn lại phạm vi mỗi lần mở.
  useEffect(() => {
    if (open) setScope(canManageTeam ? 'team' : 'personal')
  }, [open, canManageTeam])

  // Đối chiếu "Đã có" theo đúng phạm vi sẽ nhập vào (backend khử trùng như vậy).
  const existingTitles = useMemo(
    () => library.filter((q) => q.isPersonal === (scope === 'personal')).map((q) => q.title),
    [library, scope],
  )
  const [rawText, setRawText] = useState<string | null>(null)
  const preview = useMemo<ParsedCsv | null>(
    () => (rawText === null ? null : parseQuickReplyCsv(rawText, existingTitles)),
    [rawText, existingTitles],
  )

  const rows = preview?.rows ?? []
  const counts = rows.reduce(
    (acc, r) => ({ ...acc, [r.status]: acc[r.status] + 1 }),
    { ok: 0, exists: 0, duplicate: 0, error: 0 } as Record<PreviewRow['status'], number>,
  )
  const willImport = counts.ok + (onDuplicate === 'overwrite' ? counts.exists : 0)
  const tooMany = rows.length > MAX_ROWS

  function reset() {
    setFileName('')
    setRawText(null)
    setOnDuplicate('skip')
    if (fileRef.current) fileRef.current.value = ''
  }

  function close() {
    if (importer.isPending) return
    reset()
    onClose()
  }

  async function onFile(file: File | undefined) {
    if (!file) return
    if (file.size > MAX_BYTES) {
      toast.error(t('import.tooLarge'))
      return
    }
    setFileName(file.name)
    const text = await file.text()
    setRawText(text)
  }

  function submit() {
    const payload = rows
      .filter((r) => r.status === 'ok' || (onDuplicate === 'overwrite' && r.status === 'exists'))
      .map(({ title, content, category }) => ({ title, content, category }))
    if (!payload.length) return
    importer.mutate(
      { rows: payload, onDuplicate, isPersonal: scope === 'personal' },
      {
        onSuccess: (res) => {
          const parts = [t('import.resultCreated', { count: res.created })]
          if (res.updated) parts.push(t('import.resultUpdated', { count: res.updated }))
          if (res.skipped.length) parts.push(t('import.resultSkipped', { count: res.skipped.length }))
          if (res.errors.length) parts.push(t('import.resultErrors', { count: res.errors.length }))
          toast.success(t('import.resultSummary', { parts: parts.join(', ') }))
          reset()
          onClose()
        },
        onError: (e) => toast.error(apiError(e)),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('import.title')}</DialogTitle>
          <DialogDescription>
            {t('import.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
            <Button variant="outline" onClick={() => fileRef.current?.click()}>
              <FileUp /> {fileName ? t('import.chooseOther') : t('import.chooseFile')}
            </Button>
            {fileName && <span className="truncate text-sm text-muted-foreground">{fileName}</span>}
            <Button
              variant="link"
              className="ml-auto px-0"
              onClick={() => downloadCsv('mau-tra-loi-nhanh.csv', serializeQuickRepliesCsv(SAMPLE))}
            >
              <Download /> {t('import.downloadSample')}
            </Button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>{t('import.importInto')}</Label>
              <Select value={scope} onValueChange={(v) => setScope(v as 'team' | 'personal')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="team" disabled={!canManageTeam}>
                    {t('import.scopeTeam')}
                  </SelectItem>
                  <SelectItem value="personal">{t('import.scopePersonal')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('import.onDuplicate')}</Label>
              <Select value={onDuplicate} onValueChange={(v) => setOnDuplicate(v as 'skip' | 'overwrite')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">{t('import.skip')}</SelectItem>
                  <SelectItem value="overwrite">{t('import.overwrite')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {preview?.fatal && <p className="text-sm text-destructive">{preview.fatal}</p>}

          {rows.length > 0 && (
            <>
              <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                <span>{t('import.rowsCount', { count: rows.length })}</span>
                <span className="text-success">{t('import.newCount', { count: counts.ok })}</span>
                <span className="text-warning">{t('import.existsCount', { count: counts.exists })}</span>
                <span>{t('import.duplicateCount', { count: counts.duplicate })}</span>
                <span className="text-destructive">{t('import.errorCount', { count: counts.error })}</span>
              </div>
              {tooMany && (
                <p className="text-sm text-destructive">
                  {t('import.tooMany', { max: MAX_ROWS })}
                </p>
              )}
              <div className="max-h-80 overflow-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">{t('import.colLine')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('import.colTitle')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('import.colContent')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('import.colCategory')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('import.colStatus')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.line} className="border-t align-top">
                        <td className="px-3 py-2 tabular-nums text-muted-foreground">{r.line}</td>
                        <td className="max-w-[160px] truncate px-3 py-2" title={r.title}>
                          {r.title || '—'}
                        </td>
                        <td className="max-w-[260px] px-3 py-2">
                          <span className="line-clamp-2 whitespace-pre-line">{r.content || '—'}</span>
                        </td>
                        <td className="px-3 py-2">{r.category || '—'}</td>
                        <td className="px-3 py-2">
                          <Badge variant={STATUS[r.status].variant} title={r.message}>
                            {t(`import.status.${r.status}`)}
                          </Badge>
                          {r.status === 'error' && (
                            <p className="mt-1 text-xs text-destructive">{r.message}</p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={importer.isPending}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={submit} disabled={!willImport || tooMany || importer.isPending}>
            {importer.isPending && <Loader2 className="animate-spin" />}
            {willImport ? t('import.submit', { count: willImport }) : t('import.submitNone')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
